import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

import style from "./CourseLanding.module.css";
import Container from "@/layouts/container/Container";
import Icon from "@/ui/icon/Icon";
import EuFunding from "@/ui/euFunding/EuFunding";

import { useCourseReviews } from "@/features/reviews/hooks/useCourseReviews";
import { buildCourseSeo, courseMetaTags } from "@/utils/seo";
import {
	padZero,
	secondsToHoursMinutes,
	secondsToMinutesSeconds,
} from "@/utils/helpers";
import { formatTimeAgo } from "@/i18n/format";
import { useCurrentLanguage, useLocalized } from "@/i18n/useLocalized";

function CourseLanding({
	course,
	modules,
	firstUnitUrl,
	resumeUrl,
	hasProgress,
}) {
	const { courseSlug } = useParams();
	const { t } = useTranslation();
	const language = useCurrentLanguage();
	const { reviews } = useCourseReviews({ course: { slug: courseSlug } });
	const l = useLocalized();

	const units = modules.flatMap((module) => module.units ?? []);
	const videoCount = units.filter((u) => u.unit_type === "video").length;
	const quizCount = units.filter((u) => u.unit_type === "quiz").length;

	const firstUnit = modules[0]?.units?.[0];
	const entryUrl = hasProgress ? resumeUrl : firstUnitUrl;
	// Courses have no trailer, so the play affordance opens the real first
	// lesson — and only when that lesson is actually watchable, rather than
	// promising a preview that lands on the paywall.
	const previewUrl = hasProgress || firstUnit?.is_preview ? entryUrl : null;

	const rating = averageRating(reviews, language);

	return (
		<Container size="medium">
			<CourseSeo
				course={course}
				slug={courseSlug}
				moduleCount={modules.length}
			/>

			<div className={style.grid}>
				{/* Three grid children rather than two columns of content: the
				    sidebar sits between the intro and the body in source order,
				    so when the grid collapses to one column the CTA follows the
				    description instead of trailing every review on the page. */}
				<div className={style.intro}>
					<div className={style.hero}>
						{firstUnit?.thumbnail && (
							<img src={firstUnit.thumbnail} alt="" />
						)}
						<Link
							to="/"
							className={style.goBack}
							aria-label={t("courses:landing.backHome")}
						>
							<Icon icon="fa-arrow-left" iconType="fa-solid" />
						</Link>
						{previewUrl && (
							<Link to={previewUrl} className={style.preview}>
								<span className={style.play}>
									<Icon icon="fa-play" iconType="fa-solid" />
								</span>
								<span className={style.previewLabel}>
									{hasProgress
										? t("courses:landing.preview.resume")
										: t("courses:landing.preview.start")}
								</span>
							</Link>
						)}
					</div>

					<h1 className={style.title}>{l(course, "name")}</h1>

					<div className={style.meta}>
						{course.categories.length > 0 && (
							<div className={style.categories}>
								{course.categories.map((category) => (
									<Link
										key={category.slug}
										to={`/courses?category=${category.slug}`}
									>
										{l(category, "name")}
									</Link>
								))}
							</div>
						)}
						{rating && (
							<a href="#recenzije" className={style.rating}>
								<Stars value={rating.average} />
								<span className={style.ratingValue}>
									{rating.label}
								</span>
								<span className={style.ratingCount}>
									{t("courses:landing.reviewCount", {
										count: rating.count,
									})}
								</span>
							</a>
						)}
					</div>

					<CourseDescription html={l(course, "description")} />
				</div>

				<aside className={style.asideColumn}>
					<Sidebar
						course={course}
						moduleCount={modules.length}
						videoCount={videoCount}
						quizCount={quizCount}
						rating={rating}
						entryUrl={entryUrl}
						hasProgress={hasProgress}
						firstLessonFree={Boolean(firstUnit?.is_preview)}
					/>
				</aside>

				<div className={style.body}>
					<Curriculum modules={modules} />

					{course.lecturers.length > 0 && (
						<section className={style.section}>
							<h2>
								{course.lecturers.length > 1
									? t("courses:landing.lecturers")
									: t("courses:landing.lecturer")}
							</h2>
							<div
								className={`${style.lecturers} ${
									course.lecturers.length === 1
										? style.single
										: ""
								}`}
							>
								{course.lecturers.map((lecturer) => (
									<LecturerCard
										key={lecturer.name}
										lecturer={lecturer}
									/>
								))}
							</div>
						</section>
					)}

					{rating && <Reviews reviews={reviews} rating={rating} />}
				</div>
			</div>
		</Container>
	);
}

// Mirrors the tags the prerender already wrote into this page's HTML, which is
// what lets react-helmet-async adopt them instead of appending a second copy.
// See src/utils/seo.js.
function CourseSeo({ course, slug, moduleCount }) {
	const l = useLocalized();
	const language = useCurrentLanguage();
	const seo = buildCourseSeo({
		slug,
		name: l(course, "name"),
		description: l(course, "description"),
		moduleCount: course.module_count ?? moduleCount,
		language,
	});

	return (
		<Helmet>
			<title>{seo.title}</title>
			{courseMetaTags({ ...seo, language }).map((tag) => (
				<meta key={tag.name ?? tag.property} {...tag} />
			))}
			<link rel="canonical" href={seo.canonical} />
		</Helmet>
	);
}

const DESCRIPTION_LIMIT = 800;

function CourseDescription({ html }) {
	const { t } = useTranslation();
	const [isExpanded, setIsExpanded] = useState(false);
	const description = html || "";

	// Cut on a line break where there is one, otherwise on a word boundary, so
	// the collapsed body never ends mid-word or mid-tag.
	const head = description.slice(0, DESCRIPTION_LIMIT);
	const lastEnter = head.lastIndexOf("\n");
	const cut = lastEnter > 0 ? lastEnter : head.lastIndexOf(" ");
	const truncated = description.slice(0, cut + 1);
	const isTruncatable =
		description.length > DESCRIPTION_LIMIT &&
		description.length > truncated.length;

	if (!description) return null;

	return (
		<div className={style.description}>
			<div
				dangerouslySetInnerHTML={{
					__html:
						isExpanded || !isTruncatable ? description : truncated,
				}}
			/>
			{isTruncatable && (
				<button
					className={style.showMore}
					onClick={() => setIsExpanded(!isExpanded)}
				>
					{isExpanded
						? t("courses:landing.description.showLess")
						: t("courses:landing.description.showMore")}
				</button>
			)}
		</div>
	);
}

function Curriculum({ modules }) {
	const { t } = useTranslation();
	const l = useLocalized();
	// The first module opens on arrival: it is the one a visitor deciding
	// whether to start actually reads.
	const [openModules, setOpenModules] = useState(() =>
		modules[0]?.slug ? [modules[0].slug] : []
	);

	function toggle(slug) {
		setOpenModules((open) =>
			open.includes(slug)
				? open.filter((s) => s !== slug)
				: [...open, slug]
		);
	}

	if (modules.length === 0) return null;

	return (
		<section className={style.section}>
			<h2>{t("courses:landing.curriculum.title")}</h2>
			<div className={style.modules}>
				{modules.map((module) => {
					const units = module.units ?? [];
					const isOpen = openModules.includes(module.slug);

					return (
						<div key={module.slug} className={style.module}>
							<button
								className={style.moduleHeader}
								onClick={() => toggle(module.slug)}
								aria-expanded={isOpen}
							>
								<span className={style.moduleName}>
									{l(module, "name")}
								</span>
								<span className={style.moduleMeta}>
									{t("courses:landing.curriculum.lessons", {
										count: units.length,
									})}
								</span>
								<span
									className={`${style.chevron} ${
										isOpen ? style.open : ""
									}`}
								>
									<Icon
										icon="fa-chevron-down"
										iconType="fa-solid"
									/>
								</span>
							</button>
							{isOpen && (
								<ul className={style.units}>
									{units.map((unit) => (
										<li
											key={unit.slug}
											className={style.unit}
										>
											<Icon
												icon={unitIcon(unit)}
												iconType="fa-solid"
											/>
											<span className={style.unitName}>
												{l(unit, "name")}
											</span>
											{unit.is_preview && (
												<span className={style.free}>
													{t("courses:landing.curriculum.free")}
												</span>
											)}
											<span className={style.unitMeta}>
												{unitMeta(unit, t)}
											</span>
										</li>
									))}
								</ul>
							)}
						</div>
					);
				})}
			</div>
		</section>
	);
}

function unitIcon(unit) {
	if (unit.is_preview) return "fa-play";
	return unit.unit_type === "quiz" ? "fa-question" : "fa-lock";
}

function unitMeta(unit, t) {
	if (unit.unit_type === "quiz") {
		return t("courses:landing.curriculum.questions", {
			count: unit.question_count ?? 0,
		});
	}
	return unit.video_duration
		? secondsToMinutesSeconds(unit.video_duration)
		: "";
}

function LecturerCard({ lecturer }) {
	const l = useLocalized();

	return (
		<div className={style.lecturer}>
			<div className={style.lecturerHeader}>
				<div className={style.avatar}>
					{lecturer.picture_url ? (
						<img src={lecturer.picture_url} alt="" />
					) : (
						<span>{initials(lecturer.name)}</span>
					)}
				</div>
				<div>
					<div className={style.lecturerName}>
						<span>{lecturer.name}</span>
						<Icon icon="fa-badge-check" iconType="fa-solid" />
					</div>
					<div className={style.lecturerTitle}>
						{l(lecturer, "job_title")}
					</div>
				</div>
			</div>
			{l(lecturer, "bio") && (
				<p className={style.bio}>{l(lecturer, "bio")}</p>
			)}
		</div>
	);
}

const VISIBLE_REVIEWS = 6;

function Reviews({ reviews, rating }) {
	const { t } = useTranslation();
	const [showAll, setShowAll] = useState(false);
	const language = useCurrentLanguage();
	const l = useLocalized();

	// A rating with no words is still worth counting in the average, but as a
	// card it is an empty box in the middle of the grid.
	const written = reviews.filter((review) => l(review, "content")?.trim());
	const visible = showAll ? written : written.slice(0, VISIBLE_REVIEWS);

	if (written.length === 0) return null;

	return (
		<section id="recenzije" className={style.section}>
			<div className={style.reviewsHeading}>
				<h2>{t("courses:landing.reviews.title")}</h2>
				<span className={style.reviewsSummary}>
					<Icon icon="fa-star" iconType="fa-solid" />{" "}
					{t("courses:landing.reviews.summary", {
						rating: rating.label,
						count: rating.count,
					})}
				</span>
			</div>
			<div className={style.reviews}>
				{visible.map((review) => (
					<figure key={review.uuid} className={style.review}>
						<Stars value={review.stars} />
						<blockquote>{l(review, "content")}</blockquote>
						<figcaption>
							<div className={style.reviewAvatar}>
								{review.user?.safe_picture_url ? (
									<img
										src={review.user.safe_picture_url}
										alt=""
									/>
								) : (
									<span>
										{initials(review.user?.username)}
									</span>
								)}
							</div>
							<div>
								<span className={style.reviewName}>
									{review.user?.username}
								</span>
								<span className={style.reviewDate}>
									{formatTimeAgo(
										new Date(review.timestamp).getTime(),
										language
									)}
								</span>
							</div>
						</figcaption>
					</figure>
				))}
			</div>
			{written.length > visible.length && (
				<button
					className={`${style.showMore} ${style.showAllReviews}`}
					onClick={() => setShowAll(true)}
				>
					{t("courses:landing.reviews.showMore", {
						count: written.length - visible.length,
					})}
				</button>
			)}
		</section>
	);
}

function Sidebar({
	course,
	moduleCount,
	videoCount,
	quizCount,
	rating,
	entryUrl,
	hasProgress,
	firstLessonFree,
}) {
	const { t } = useTranslation();
	const difficulty = ["easy", "medium", "hard"].includes(course.difficulty)
		? course.difficulty
		: "easy";
	const details = [
		{
			icon: "fa-film",
			label: t("courses:landing.details.lessons"),
			value: padZero(videoCount),
		},
		{
			icon: "fa-bullseye",
			label: t("courses:difficulty.label"),
			value: t(`courses:difficulty.${difficulty}`),
		},
		{
			icon: "fa-question",
			label: t("courses:landing.details.quizzes"),
			value: padZero(quizCount),
		},
		{
			icon: "fa-user",
			label: t("courses:landing.details.lecturers"),
			value: padZero(course.lecturers.length),
		},
		rating && {
			icon: "fa-star",
			label: t("courses:landing.details.reviews"),
			value: rating.label,
		},
		{
			icon: "fa-award",
			label: t("courses:landing.details.certificate"),
			value: t("courses:landing.details.certificateValue"),
		},
	].filter(Boolean);

	return (
		<div className={style.sidebar}>
			<span className={style.sidebarLabel}>
				{t("courses:landing.sidebar.contains")}
			</span>
			<div className={style.summary}>
				<div className={style.summaryItem}>
					<Icon icon="fa-layer-group" />
					<div>
						<div className={style.summaryLabel}>
							{t("courses:landing.sidebar.modules", {
								count: moduleCount,
							})}
						</div>
						<div className={style.summaryValue}>{moduleCount}</div>
					</div>
				</div>
				<div className={style.summaryItem}>
					<Icon icon="fa-clock" />
					<div>
						<div className={style.summaryLabel}>
							{t("courses:landing.sidebar.duration")}
						</div>
						<div className={style.summaryValue}>
							{course.duration
								? secondsToHoursMinutes(course.duration)
								: "—"}
						</div>
					</div>
				</div>
			</div>

			<div className={style.details}>
				{details.map((detail) => (
					<div key={detail.label} className={style.detail}>
						<Icon icon={detail.icon} />
						<span className={style.detailLabel}>
							{detail.label}:
						</span>
						<span>{detail.value}</span>
					</div>
				))}
			</div>

			<div className={style.cta}>
				<Link to={entryUrl} className={style.ctaButton}>
					<span>
						{hasProgress
							? t("courses:landing.sidebar.resumeCourse")
							: t("courses:landing.sidebar.startCourse")}
					</span>
					<Icon icon="fa-arrow-right" iconType="fa-solid" />
				</Link>
				{firstLessonFree && (
					<span className={style.ctaNote}>
						{t("courses:landing.sidebar.firstLessonFree")}
					</span>
				)}
			</div>

			<div className={style.certificate}>
				<div className={style.certificateIcon}>
					<Icon icon="fa-award" />
				</div>
				<div>
					<div className={style.certificateTitle}>
						{t("courses:landing.sidebar.certificateTitle")}
					</div>
					<p>{t("courses:landing.sidebar.certificateText")}</p>
				</div>
			</div>

			{/* The free part of the catalogue is what the EU grant paid for,
			    so the attribution rides on `is_free` from the course itself
			    rather than on any list of courses. */}
			{course.is_free && (
				<EuFunding statement className={style.euFunding} />
			)}
		</div>
	);
}

function Stars({ value }) {
	// Rounded to the nearest half so the row always reads as the number beside
	// it — 4.8 shows as four and a half, not five.
	const halves = Math.round(value * 2);

	return (
		<span className={style.stars} aria-hidden="true">
			{[0, 1, 2, 3, 4].map((i) => {
				const filled = halves - i * 2;
				if (filled >= 2)
					return <Icon key={i} icon="fa-star" iconType="fa-solid" />;
				if (filled === 1)
					return (
						<Icon
							key={i}
							icon="fa-star-half-stroke"
							iconType="fa-solid"
						/>
					);
				return <Icon key={i} icon="fa-star" />;
			})}
		</span>
	);
}

function averageRating(reviews, language) {
	if (!reviews || reviews.length === 0) return null;

	const average =
		reviews.reduce((sum, review) => sum + (review.stars ?? 0), 0) /
		reviews.length;

	// Bosnian writes the decimal with a comma (4,8), English with a point.
	const written = average.toFixed(1);

	return {
		average,
		count: reviews.length,
		label: language === "en" ? written : written.replace(".", ","),
	};
}

function initials(name) {
	return (name || "")
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0].toUpperCase())
		.join("");
}

export default CourseLanding;
